import type { ReactNode } from 'react';
import { fmtDayFull } from '../lib/dates';
import type { CandidateWithdrawal } from '../types';
import { cx } from './charte';

// Lignes de « Mes documents » et de « Conditions de vente » (T14, 26/10/2026 : sorties de DocumentsView pour servir
// aux deux pages). Sur téléphone, les boutons passent sous le texte.

export function DocumentRow({ icon, tone = 'indigo', title, titleId, sub, children }: {
  icon: ReactNode;
  tone?: 'indigo' | 'ok' | 'warn';
  title: ReactNode;
  /** `id` du titre, pour décrire un bouton ou un lien de la ligne (`aria-describedby`). */
  titleId?: string;
  sub?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3.5 first:pt-0 last:pb-0">
      <span
        className={cx(
          'grid h-10 w-10 shrink-0 place-items-center rounded-xl',
          tone === 'ok' ? 'bg-success-light text-success' : tone === 'warn' ? 'bg-warn-bg text-warn-fg' : 'bg-surface-indigo text-primary',
        )}
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <p id={titleId} className="truncate text-sm font-semibold text-heading">{title}</p>
        {sub && <div className="text-xs text-secondary">{sub}</div>}
      </div>
      {children && <div className="flex shrink-0 items-center gap-2 max-sm:basis-full max-sm:pl-[52px]">{children}</div>}
    </li>
  );
}

/** Date ou heure jamais coupée en fin de ligne (« vendredi 5 » puis « novembre 2027 ») : elle passe à la ligne entière. */
function Unbroken({ children }: { children: string }) {
  return <span className="whitespace-nowrap">{children}</span>;
}

/**
 * Ligne de la rétractation d'une vente à distance : délai encore ouvert (page « Conditions de vente » seulement), ou
 * rétractation reçue. Rien pour un délai terminé.
 */
export function WithdrawalLine({ sale }: { sale?: CandidateWithdrawal }) {
  if (sale?.state === 'open')
    return (
      <span className="mt-0.5 block">
        {'Rétractation possible jusqu’au '}
        <Unbroken>{fmtDayFull(sale.until)}</Unbroken>
        {' inclus'}
      </span>
    );
  if (sale?.state === 'withdrawn' && sale.withdrawal)
    return (
      <span className="mt-0.5 block">
        {'Rétractation reçue le '}
        <Unbroken>{fmtDayFull(sale.withdrawal.receivedOn)}</Unbroken>
        {sale.withdrawal.receivedTime && (
          <>
            {' à '}
            <Unbroken>{sale.withdrawal.receivedTime}</Unbroken>
          </>
        )}
      </span>
    );
  return null;
}
